/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';

import { BrandHeading } from '@af/design-system-docs-ui/brand-heading';
import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Image from '@atlaskit/image/image';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { patternGroups } from './_content/_patterns';
import { Guidance } from './_guidance';

const styles = cssMap({
	group: { display: 'flex', flexDirection: 'column', gap: token('space.300') },
	row: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr)',
		gap: token('space.300'),
		alignItems: 'center',
		'@media (min-width: 48rem)': {
			gridTemplateColumns: 'minmax(0, 500fr) minmax(0, 232fr)',
		},
	},
	copy: { display: 'flex', flexDirection: 'column', gap: token('space.150') },
	image: { display: 'block', width: '100%', height: 'auto', minWidth: 0 },
	paragraph: { marginBlock: token('space.0'), font: token('font.body') },
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		marginBlock: token('space.0'),
		paddingInlineStart: token('space.250'),
		font: token('font.body'),
	},
	listItem: { marginBlock: token('space.0') },
	action: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		columnGap: token('space.400'),
		rowGap: token('space.100'),
	},
});

export const Patterns = ({ name }: { name: keyof typeof patternGroups }): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { title, summary, patterns } = patternGroups[name];
	const panelId = `amp-${name}-patterns`;
	const summaryId = `${panelId}-summary`;
	const label = `${isExpanded ? 'Hide' : 'Show'} ${patterns.length} patterns`;

	return (
		<div css={styles.group}>
			<div id={panelId} hidden={!isExpanded}>
				<div css={styles.group}>
					{patterns.map(
						({
							title: patternTitle,
							description,
							image,
							introduction,
							additionalImages,
							details,
							guidance,
						}) => (
							<div key={patternTitle} css={styles.group}>
								<div css={styles.row}>
									<div css={styles.group}>
										<Image
											src={image.src}
											srcDark={image.srcDark}
											alt={image.alt}
											css={styles.image}
										/>
										{additionalImages?.map((additionalImage) => (
											<Image
												key={additionalImage.src}
												src={additionalImage.src}
												srcDark={additionalImage.srcDark}
												alt={additionalImage.alt}
												css={styles.image}
											/>
										))}
									</div>
									<div css={styles.copy}>
										<BrandHeading as="h4" size="small">
											{patternTitle}
										</BrandHeading>
										{introduction && <p css={styles.paragraph}>{introduction}</p>}
										{Array.isArray(description) ? (
											<ol css={styles.list}>
												{description.map((item) => (
													<li key={item} css={styles.listItem}>
														<strong>{item.slice(0, item.indexOf(':') + 1)}</strong>
														{item.slice(item.indexOf(':') + 1)}
													</li>
												))}
											</ol>
										) : (
											<p css={styles.paragraph}>{description}</p>
										)}
									</div>
								</div>
								{details?.map((detail) => (
									<div key={detail.image.src} css={styles.row}>
										<Image
											src={detail.image.src}
											srcDark={detail.image.srcDark}
											alt={detail.image.alt}
											css={styles.image}
										/>
										<p css={styles.paragraph}>{detail.description}</p>
									</div>
								))}
								{guidance && <Guidance name={guidance} />}
							</div>
						),
					)}
				</div>
			</div>
			<div css={styles.action}>
				<Button
					appearance="subtle"
					aria-controls={panelId}
					aria-describedby={isExpanded ? undefined : summaryId}
					aria-expanded={isExpanded}
					aria-label={`${label} for ${title}`}
					iconAfter={() =>
						isExpanded ? (
							<ChevronUpIcon label="" size="small" />
						) : (
							<ChevronRightIcon label="" size="small" />
						)
					}
					onClick={() => setIsExpanded((expanded) => !expanded)}
				>
					{label}
				</Button>
				{!isExpanded && (
					<Text id={summaryId} color="color.text.subtle">
						{summary}
					</Text>
				)}
			</div>
		</div>
	);
};
